import {
  addCalendarDays,
  calendarWeekday,
  romaniaDateKey,
  romaniaInstant,
  ROMANIA_TIME_ZONE,
} from './romania-calendar';

/**
 * How the scheduler's days and times of day map onto instants — the API's
 * engine and the board's planner read them alike. The scheduler thinks in
 * calendar days ("YYYY-MM-DD"), in times of day (minutes from 00:00, as the
 * programs store them) and in weekdays; a frame says which instants those are.
 * Every loop over days walks a frame (`dayStart`, then `nextDay`), and every
 * key a day is memoised or matched on is the instant its frame says it starts.
 *
 * UTC — the scheduler's original frame: a day is 24 h from UTC midnight, and
 * 08:00 is 08:00 UTC. Every calendar runs on it until the shop saves its own
 * (and every engine test): it is the arithmetic the scheduler always had.
 *
 * ROMANIA — Europe/Bucharest, the frame of the shop's saved calendar: 08:00 is
 * 08:00 in Romania, summer time included, and a day runs from midnight to
 * midnight there — 23 or 25 hours on the two changeover Sundays.
 */
export interface TimeFrame {
  readonly zone: 'UTC' | typeof ROMANIA_TIME_ZONE;
  /** The start of the day the instant `ms` falls in. */
  dayStart(ms: number): number;
  /** The start of the day after the one starting at `dayStartMs`. */
  nextDay(dayStartMs: number): number;
  /** The start of the day before the one starting at `dayStartMs`. */
  previousDay(dayStartMs: number): number;
  /** `YYYY-MM-DD` of the day starting at `dayStartMs`. */
  dateKey(dayStartMs: number): string;
  /** The start of the day `date` (`YYYY-MM-DD`); NaN when it is not a date. */
  dayOf(date: string): number;
  /** The weekday of the day starting at `dayStartMs`: 0 = Sunday … 6 = Saturday. */
  weekday(dayStartMs: number): number;
  /** The instant `minutes` past 00:00 of the day starting at `dayStartMs` (1440 = its end). */
  at(dayStartMs: number, minutes: number): number;
}

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** The original frame — exactly the UTC-midnight arithmetic the scheduler always ran. */
export const UTC_FRAME: TimeFrame = Object.freeze({
  zone: 'UTC' as const,
  dayStart: (ms: number) => Math.floor(ms / DAY_MS) * DAY_MS,
  nextDay: (dayStartMs: number) => dayStartMs + DAY_MS,
  previousDay: (dayStartMs: number) => dayStartMs - DAY_MS,
  dateKey: (dayStartMs: number) => new Date(dayStartMs).toISOString().slice(0, 10),
  dayOf: (date: string) => Date.parse(`${date}T00:00:00.000Z`),
  weekday: (dayStartMs: number) => new Date(dayStartMs).getUTCDay(),
  at: (dayStartMs: number, minutes: number) => dayStartMs + minutes * MINUTE_MS,
});

// A day's date, and its neighbours, are asked for thousands of times per
// projection: memoised by the day's start. Bounded by the days the scheduler
// ever looks at — a few years of them.
const romaniaDates = new Map<number, string>();
const romaniaNext = new Map<number, number>();
const romaniaPrevious = new Map<number, number>();

function romaniaDateOf(dayStartMs: number): string {
  let date = romaniaDates.get(dayStartMs);
  if (date === undefined) {
    date = romaniaDateKey(dayStartMs);
    romaniaDates.set(dayStartMs, date);
  }
  return date;
}

/** The shop's frame once its calendar is saved: Romania's own clock. */
export const ROMANIA_FRAME: TimeFrame = Object.freeze({
  zone: ROMANIA_TIME_ZONE,
  dayStart: (ms: number) => romaniaInstant(romaniaDateKey(ms), 0),
  nextDay: (dayStartMs: number) => {
    let next = romaniaNext.get(dayStartMs);
    if (next === undefined) {
      next = romaniaInstant(addCalendarDays(romaniaDateOf(dayStartMs), 1), 0);
      romaniaNext.set(dayStartMs, next);
    }
    return next;
  },
  previousDay: (dayStartMs: number) => {
    let previous = romaniaPrevious.get(dayStartMs);
    if (previous === undefined) {
      previous = romaniaInstant(addCalendarDays(romaniaDateOf(dayStartMs), -1), 0);
      romaniaPrevious.set(dayStartMs, previous);
    }
    return previous;
  },
  dateKey: romaniaDateOf,
  dayOf: (date: string) => romaniaInstant(date, 0),
  weekday: (dayStartMs: number) => calendarWeekday(romaniaDateOf(dayStartMs)),
  at: (dayStartMs: number, minutes: number) => romaniaInstant(romaniaDateOf(dayStartMs), minutes),
});
